import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  MachineUpdateView,
  UpdateChannel,
  UpdateJobView,
  UpdatePrepareResponse,
} from "@racler/protocol";
import { isJobActive } from "./desktopUpdates.ts";
import { useRelay } from "./relayContext.tsx";

/** Safety net while a job runs; `system/machine.update` events are the primary source. */
const ACTIVE_JOB_POLL_MS = 15_000;

const toMap = (machines: MachineUpdateView[]): Map<string, MachineUpdateView> =>
  new Map(machines.map((m) => [m.machineId, m]));

/** Remote Desktop update state of all Machines (by machineId), kept live by `system/machine.update` events. */
export function useMachineUpdates(): {
  views: Map<string, MachineUpdateView> | null;
  error: string | null;
  reload: () => void;
  /** Stores the channel only; never installs anything. */
  setChannel: (machineId: string, channel: UpdateChannel) => Promise<void>;
  prepare: (machineId: string) => Promise<UpdatePrepareResponse>;
  start: (machineId: string, snapshotId: string, requestId: string) => Promise<UpdateJobView>;
} {
  const relay = useRelay();
  const [views, setViews] = useState<Map<string, MachineUpdateView> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const replace = useCallback((machine: MachineUpdateView) => {
    setViews((prev) => {
      const next = new Map(prev ?? []);
      next.set(machine.machineId, machine);
      return next;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      relay
        .server("system/machine.updates", {})
        .then((r) => {
          if (cancelled) return;
          setViews(toMap(r.machines));
          setError(null);
        })
        .catch((e: unknown) => !cancelled && setError(String((e as Error).message)));
    };
    const offState = relay.onState((s) => s === "open" && load());
    if (relay.connectionState === "open") load();
    const offEvents = relay.on((msg) => {
      if (msg.channel !== "system") return;
      if (msg.type === "machine.update") {
        const { machine } = msg.payload as { machine: MachineUpdateView };
        if (!cancelled && machine?.machineId) replace(machine);
      } else if (msg.type === "machine.status") {
        load(); // presence changes blockers and availability
      }
    });
    return () => {
      cancelled = true;
      offState();
      offEvents();
    };
  }, [relay, reloadKey, replace]);

  const anyActive = useMemo(
    () => (views ? [...views.values()].some((v) => isJobActive(v.job)) : false),
    [views],
  );

  useEffect(() => {
    if (!anyActive) return;
    let cancelled = false;
    const timer = setInterval(() => {
      if (relay.connectionState !== "open") return;
      relay
        .server("system/machine.updates", {})
        .then((r) => !cancelled && setViews(toMap(r.machines)))
        .catch(() => undefined);
    }, ACTIVE_JOB_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [relay, anyActive]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const setChannel = useCallback(
    async (machineId: string, channel: UpdateChannel) => {
      const r = await relay.server("system/machine.update.setChannel", { machineId, channel });
      replace(r.machine);
    },
    [relay, replace],
  );

  const prepare = useCallback(
    (machineId: string) => relay.server("system/machine.update.prepare", { machineId }),
    [relay],
  );

  const start = useCallback(
    async (machineId: string, snapshotId: string, requestId: string) => {
      const r = await relay.server("system/machine.update.start", {
        machineId,
        snapshotId,
        requestId,
        acknowledgeInterruption: true,
      });
      setViews((prev) => {
        const current = prev?.get(machineId);
        if (!prev || !current) return prev;
        const next = new Map(prev);
        next.set(machineId, { ...current, job: r.job });
        return next;
      });
      return r.job;
    },
    [relay],
  );

  return { views, error, reload, setChannel, prepare, start };
}
